"use client";

import { useEffect, useState } from "react";
import { Modal, Button, Tag, Typography, Divider, Input } from "antd";
import { Row, Col, Container } from "react-bootstrap";
import PropTypes from "prop-types";
import MedicalDetailModal from "./MedicalDetailModal";
import { useAuth } from "../../services/authentication_API/authService";
import {
  addMedicalHistoryData,
  addMedicalHistoryItem,
  getMedicalHistoryItems,
  getPremedicalHistoryData,
} from "../../services/doctor/medical_history/medicalHistory";
import { useMessageApi } from "../../services/message api/MessageProvider";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

export default function MedicalHistory({ open, onClose, patientId }) {
  const [activeSection, setActiveSection] = useState(0);
  const [selectedData, setSelectedData] = useState({});
  const [search, setSearch] = useState({});
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [medicalDetails, setMedicalDetails] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const { userData } = useAuth();
  const [sections, setSections] = useState([]); // To store the list of categories
  const messageApi = useMessageApi();
  const [searchResults, setSearchResults] = useState({});
  const [searchLoading, setSearchLoading] = useState({});
  const [significantHistory, setSignificantHistory] = useState("");
const [hospitalization, setHospitalization] = useState("");


  const toggle = (section, itemObj) => {
    // itemObj is the whole object: { id: 3, name: "T2DM", ... }
    const sectionName = section.name; // Get the category name string
    appendItemToSectionIfMissing(section.id, itemObj);

    setSelectedData((prev) => {
      const current = prev[sectionName] || [];

      // Check if the item is already selected by comparing IDs
      const isAlreadySelected = current.some(
        (selected) => selected.id === itemObj.id
      );
      console.log("Selected Data:", selectedData);
      if (!isAlreadySelected) {
        // Logic for selecting a NEW item
        setActiveItem(itemObj);
        setDetailModalOpen(true);
        setSelectedSection(sectionName);

        return {
          ...prev,
          [sectionName]: [...current, itemObj],
        };
      } else {
        // Logic for UNSELECTING an item
        // Remove it from selectedData
        const updatedSectionData = current.filter(
          (selected) => selected.id !== itemObj.id
        );

        // Also remember to remove it from your medicalDetails state
        // so it doesn't show up in the final API payload
        setMedicalDetails((prevDetails) =>
          prevDetails.filter((detail) => detail.id !== itemObj.id)
        );

        return {
          ...prev,
          [sectionName]: updatedSectionData,
        };
      }
    });
  };

  const handleSearchChange = async (sectionName, sectionId, value) => {
    const query = value.toLowerCase();

    setSearch((prev) => ({
      ...prev,
      [sectionName]: value,
    }));

    // 🔹 If less than 3 chars → clear API results
    if (query.length < 3) {
      setSearchResults((prev) => ({
        ...prev,
        [sectionName]: [],
      }));
      return;
    }

    try {
      setSearchLoading((prev) => ({ ...prev, [sectionName]: true }));

      // 🔥 CALL SEARCH API HERE
      const result = await getMedicalHistoryItems(
        query,
        userData?.id,
        sectionId
      );

      if (result.success) {
        setSearchResults((prev) => ({
          ...prev,
          [sectionName]: result.data || [],
        }));
      }
    } catch (err) {
      console.error("Search error", err);
    } finally {
      setSearchLoading((prev) => ({ ...prev, [sectionName]: false }));
    }
  };

  const fetchPreconsultData = async () => {
    try {
      setLoading(true);
      console.log("doctor id:", userData?.id);
      const result = await getPremedicalHistoryData(patientId);
      if (result.success) {
        console.log("Formatted Data:", result);
        const { pre_medical_histories, consult_medical_histories, significant_history, hospitalization } =
          result.data;
        setSections(pre_medical_histories);
        setSignificantHistory(significant_history || "");
        setHospitalization(hospitalization || "");

        // Inside your useEffect after fetching API data:
        const initialSelected = {};
        const initialDetails = [];

        consult_medical_histories.forEach((category) => {
          if (category.medical_histories.length > 0) {
            // 1. Populate the tags visual state
            initialSelected[category.name] = category.medical_histories.map(
              (item) => ({
                ...item, // This includes id, medical_history_category_id, name, etc.
              })
            );
          }
        });
        console.log("Initial Selected Data:", initialSelected);

        setSelectedData(initialSelected);
      } else {
        console.error("Error:", result.error || "Failed to fetch Appointments");
      }
    } catch (err) {
      console.error("Exception:", err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchPreconsultData();
  }, []);

  const handleSave = async () => {
    const allSelectedItems = Object.values(selectedData).flat();

    const payload = {
      hospital_id: userData?.parent_user_id,
      doctor_id: userData?.id,
      appointment_id: null,
      significant_history: significantHistory,
      hospitalization: hospitalization,
      medical_histories: allSelectedItems.map((item) => {
        return {
          ...item,
        };
      }),
    };
    try {
      const result = await addMedicalHistoryData(payload, patientId);
      if (result.success) {
        console.log("auto saved:", result);
        messageApi.success("Medical history saved successfully");
      } else {
        console.error(
          "Error:",
          result.error || "Failed to autosave consultant"
        );
      }
    } catch (err) {
      console.error("Exception:", err.message);
    }
    console.log("Final Payload:", payload);
  };

  const appendItemToSectionIfMissing = (sectionId, item) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;

        const exists = section.medical_histories.some((i) => i.id === item.id);

        if (exists) return section;

        return {
          ...section,
          medical_histories: [...section.medical_histories, item],
        };
      })
    );
  };
  const onclose =()=>{
    handleSave()
    onClose()
  }

  const handleDetailSave = (detailData) => {
    setSelectedData((prev) => {
      const sectionItems = prev[selectedSection] || [];

      const updatedItems = sectionItems.map((item) => {
        if (item.id !== activeItem.id) return item;

        let updatedItem = {
          ...item,
          note: detailData.note,
        };

        // 🔹 Procedure history
        if (selectedSection === "H/O Procedures") {
          updatedItem.since_when_year = detailData.since_when_year;
        }

        // 🔹 Family history
        else if (selectedSection === "Family History") {
          updatedItem.family_relation = detailData.family_relation;
        }

        // 🔹 Normal medical history
        else {
          updatedItem.since_year = detailData.selectedYears;
          updatedItem.since_month = detailData.selectedMonths;
        }
        console.log("Updated Item:", updatedItem);
        return updatedItem;
      });

      return {
        ...prev,
        [selectedSection]: updatedItems,
      };
    });
  };
  const handleAddNewItem = async (query, sectionObj) => {
    if (!query || query.length < 3) return;

    try {
      setSearchLoading((prev) => ({ ...prev, [sectionObj.name]: true }));

      // 🔥 CALL ADD API
      const result = await addMedicalHistoryItem({
        doctor_id: userData?.id,
        medical_history_category_id: sectionObj.id,
        name: query,
      });

      if (result.success) {
        const newItem = result.data; // must contain id + name

        // ✅ 1. Append into sections (so CheckableTag shows)
        setSections((prev) =>
          prev.map((sec) =>
            sec.id === sectionObj.id
              ? {
                  ...sec,
                  medical_histories: [...sec.medical_histories, newItem],
                }
              : sec
          )
        );

        // ✅ 2. Mark as selected
        setSelectedData((prev) => ({
          ...prev,
          [sectionObj.name]: [...(prev[sectionObj.name] || []), newItem],
        }));

        // ✅ 3. Open detail modal
        setActiveItem(newItem);
        setSelectedSection(sectionObj.name);
        setDetailModalOpen(true);

        // ✅ 4. Clear search
        setSearch((prev) => ({
          ...prev,
          [sectionObj.name]: "",
        }));

        messageApi.success("Item added successfully");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to add item");
    } finally {
      setSearchLoading((prev) => ({ ...prev, [sectionObj.name]: false }));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onclose}
      closeIcon={
    <CloseOutlined style={{ fontSize: '22px', fontWeight: 'bold' }} />
  }
      footer={[
        // <Button key="save" type="primary" onClick={handleSave}>
        //   Save
        // </Button>,
        // <Button key="close" onClick={onClose}>
        //   Close
        // </Button>,
      ]}
      width="95%"
      style={{ top: 10 }}
      bodyStyle={{ padding: 0, maxHeight: "85vh", overflowY: "auto", msOverflowStyle: 'none', scrollbarWidth: 'none',      /* Firefox */    /* IE and Edge */}}
      destroyOnClose
      
    >
      <Container fluid className="bg-light h-100">
        <Row className="h-100">
          {/* MAIN CONTENT */}
          <Col
            md={9}
            className="px-4 h-100"
            style={{
              height: "90vh",
              overflowY: "auto",
              msOverflowStyle: 'none',     /* IE and Edge */
  scrollbarWidth: 'none',      /* Firefox */
            }}
          >
            {sections.map((sectionObj, index) => (
              <div key={sectionObj.id} id={`section-${index}`} className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Title level={4} underline className="m-0">
                    {index + 1}. {sectionObj.name}
                  </Title>

                  <Input
                    placeholder="Search..."
                    allowClear
                    style={{ width: 220 }}
                    value={search[sectionObj.name] || ""}
                    onChange={(e) =>
                      handleSearchChange(
                        sectionObj.name,
                        sectionObj.id,
                        e.target.value
                      )
                    }
                    suffix={
                      <PlusOutlined
                        style={{
                          color:
                            (search[sectionObj.name] || "").length >= 3
                              ? "#1677ff"
                              : "#ccc",
                          cursor:
                            (search[sectionObj.name] || "").length >= 3
                              ? "pointer"
                              : "not-allowed",
                        }}
                        onClick={() => {
                          if ((search[sectionObj.name] || "").length >= 3) {
                            handleAddNewItem(
                              search[sectionObj.name],
                              sectionObj
                            );
                          }
                        }}
                      />
                    }
                  />
                </div>

                <Text>Select options</Text>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  {(() => {
                    const searchQuery = search[sectionObj.name] || "";
                    const apiItems = searchResults[sectionObj.name] || [];

                    const itemsToShow =
                      searchQuery.length >= 3
                        ? apiItems
                        : sectionObj.medical_histories.filter((item) =>
                            item?.name?.toLowerCase().includes(searchQuery)
                          );

                    return itemsToShow.map((item) => (
                      <Tag.CheckableTag
                        key={item.id}
                        checked={selectedData[sectionObj.name]?.some(
                          (selected) => selected?.id === item?.id
                        )}
                        onChange={() => toggle(sectionObj, item)}
                        className="p-2 px-3 fs-6"
                        style={{
                          padding: "8px 14px",
                          borderRadius: "25px",
                          border: "1px solid #ddd",
                          cursor: "pointer",
                        }}
                      >
                        {item.name}
                      </Tag.CheckableTag>
                    ));
                  })()}

                  {/* <Button
                    style={{
                      padding: "8px 14px",
                      borderRadius: "25px",
                      border: "1px solid #ddd",
                      cursor: "pointer",
                      height: "100%",
                    }}
                  >
                    + Add More
                  </Button> */}
                </div>
              </div>
            ))}
            <div className="mb-4">
              <Title level={4}>Significant History</Title>
              <Input.TextArea
                rows={3}
                placeholder="Enter significant medical history..."
                value={significantHistory}
                onChange={(e) => setSignificantHistory(e.target.value)}
              />
            </div>

            {/* ✅ HOSPITALIZATION TEXTBOX */}
            <div className="mb-5">
              <Title level={4}>Hospitalization</Title>
              <Input.TextArea
                rows={3}
                placeholder="Enter hospitalization details..."
                value={hospitalization}
                onChange={(e) => setHospitalization(e.target.value)}
              />
            </div>
          </Col>

          {/* SIDEBAR */}
          <Col
            md={3}
            className="border-start bg-white"
            style={{ overflowY: "auto", height: "90vh" ,msOverflowStyle: 'none',     /* IE and Edge */
  scrollbarWidth: 'none',      /* Firefox */}}
          >
            <div style={{ padding: "16px" }}>
              <Title level={5}>Selected Medical History</Title>

              {Object.keys(selectedData).length === 0 && (
                <Text type="secondary">No items selected</Text>
              )}

              {Object.entries(selectedData).map(([sectionName, items]) => {
                if (!items || items.length === 0) return null;

                return (
                  <div key={sectionName} className="mb-4">
                    <Text strong>{sectionName}</Text>
                    <Divider className="my-2" />

                    {items.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          padding: "10px",
                          border: "1px solid #f0f0f0",
                          borderRadius: "8px",
                          marginBottom: "8px",
                          background: "#fafafa",
                        }}
                      >
                        <Text strong>{item.name}</Text>

                        {/* NOTE */}
                        {item.note && (
                          <div>
                            <Text type="secondary">Note:</Text>{" "}
                            <Text>{item.note}</Text>
                          </div>
                        )}

                        {/* NORMAL HISTORY */}
                        {(item.since_year || item.since_month) && (
                          <div>
                            <Text type="secondary">Since:</Text>{" "}
                            <Text>
                              {item.since_year
                                ? `${item.since_year} year `
                                : ""}
                              {item.since_month
                                ? `${item.since_month} month`
                                : ""}
                            </Text>
                          </div>
                        )}

                        {/* PROCEDURE HISTORY */}
                        {item.since_when_year && (
                          <div>
                            <Text type="secondary">Procedure Year:</Text>{" "}
                            <Text>{item.since_when_year}</Text>
                          </div>
                        )}

                        {/* FAMILY HISTORY */}
                        {item.family_relation && (
                          <div>
                            <Text type="secondary">Relation:</Text>{" "}
                            <Text>{item.family_relation}</Text>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </Col>

          {/* DETAIL MODAL */}

          {![
            "Drug Allergies",
            "General Allergies",
            "High Risk Factors",
            "Diet, Sleep, Exercise",
            "Other Symptoms",
          ].includes(selectedSection) && (
            <MedicalDetailModal
              open={detailModalOpen}
              onClose={() => setDetailModalOpen(false)}
              selectedItem={activeItem}
              type={selectedSection}
              onSave={handleDetailSave}
            />
          )}
        </Row>
      </Container>
    </Modal>
  );
}

MedicalHistory.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientId: PropTypes.number.isRequired,
};
